import React, { useState, useCallback, useRef } from 'react'
import { message } from 'antd'
import { FormInstance } from 'antd/lib/form'
import { materialTypeEnum } from '@/global/enum/product'
import { POST_PRODUCT_PRECHECK, PreCheckErrors } from '@/api/MaterialApi'

// 导出错误类型，供外部使用
export type { PreCheckErrors }
import PreCheckModal, {
  PreCheckErrorItem,
  PreCheckStatus,
} from '@/pages/goodsmanage/components/PreCheckModal'
import {
  preReviewOfMaterialsTypeEnum,
  preReviewOfMaterialsTypeEnumValue,
} from '@/global/enum/product'

export interface UsePreCheckOptions {
  form: FormInstance // 表单实例，用于获取productType
  onSubmit: (params: any) => Promise<void> // 实际提交函数
  onModify?: (errors: PreCheckErrors) => void // 立即修改回调，返回错误信息对象
  onProcessLater?: () => void // 稍后处理回调
}

export interface UsePreCheckReturn {
  // 执行预审的函数
  executePreCheck: (
    params: any,
    type: preReviewOfMaterialsTypeEnumValue,
  ) => Promise<void>
  // 预审弹框组件
  PreCheckModalComponent: React.ReactNode
  // 提交是否进行中，供调用页面禁用提交按钮
  submitting: boolean
}

/**
 * 物料预审 Hook
 * 用于在提交物料前进行预审校验
 */
export const usePreCheck = (options: UsePreCheckOptions): UsePreCheckReturn => {
  const { form, onSubmit, onModify, onProcessLater } = options

  // 预审弹框状态
  const [preCheckVisible, setPreCheckVisible] = useState(false)
  const [preCheckStatus, setPreCheckStatus] =
    useState<PreCheckStatus>('loading')
  const [preCheckErrors, setPreCheckErrors] = useState<PreCheckErrors>({})
  const [preCheckErrorMessages, setPreCheckErrorMessages] = useState<
    PreCheckErrorItem[]
  >([]) // 用于弹框显示的错误信息
  const [preCheckReviewer, setPreCheckReviewer] = useState<string>('')
  const [preCheckParams, setPreCheckParams] = useState<any>(null)
  // 提交进行中：物料提报接口耗时较长，期间必须挡住重复提交
  const [submitting, setSubmitting] = useState(false)
  // state 更新是异步的，同一轮事件里连点拿不到最新值，用 ref 即时兜住
  const submittingRef = useRef(false)

  /**
   * 提交并维护 submitting 状态，提交进行中重复调用直接忽略
   */
  const runSubmit = useCallback(
    async (params: any) => {
      if (submittingRef.current) {
        return
      }
      submittingRef.current = true
      setSubmitting(true)
      try {
        await onSubmit(params)
      } finally {
        submittingRef.current = false
        setSubmitting(false)
      }
    },
    [onSubmit],
  )

  /**
   * 判断是否是电商供应商物料
   */
  const isEcommerceSupplier = useCallback((): boolean => {
    const productType = form.getFieldValue('productType')
    return materialTypeEnum.getLabelByValue(productType) === '电商供应商物料'
  }, [form])

  /**
   * 执行预审
   */
  const executePreCheck = useCallback(
    async (params: any, type: preReviewOfMaterialsTypeEnumValue) => {
      // 如果不是电商供应商物料，直接提交
      if (!isEcommerceSupplier()) {
        await runSubmit(params)
        return
      }

      // 显示预审中弹框
      setPreCheckVisible(true)
      setPreCheckStatus('loading')
      setPreCheckParams(params)
      message.destroy()

      let preCheckParams: any = {
        preReviewOfMaterialsType: type,
      }
      if (preReviewOfMaterialsTypeEnum.isLabelsMatchValue(['新增物料'], type)) {
        preCheckParams = { ...preCheckParams, productAddParam: params }
      } else if (
        preReviewOfMaterialsTypeEnum.isLabelsMatchValue(['单条提报'], type)
      ) {
        preCheckParams = { ...preCheckParams, materialReportUpdateForm: params }
      } else if (
        preReviewOfMaterialsTypeEnum.isLabelsMatchValue(['提报驳回更新'], type)
      ) {
        preCheckParams = {
          ...preCheckParams,
          materialReportSaveNewForm: params,
        }
      }
      // 调用预审接口
      const preCheckRes = await POST_PRODUCT_PRECHECK(preCheckParams)
      if (preCheckRes.code === 0 && preCheckRes.data) {
        const { success, errorList, reviewer } = preCheckRes.data

        if (success) {
          // 预审成功
          setPreCheckStatus('success')
          setPreCheckReviewer(reviewer || '')
        } else {
          // 预审失败
          const errors: PreCheckErrors = errorList || {}
          setPreCheckStatus('failed')
          setPreCheckErrors(errors)
          // 提取所有错误信息用于弹框显示
          const errorMessages: PreCheckErrorItem[] = [
            errors.marketPriceError,
            errors.picturesError,
            errors.detailsPicturesError,
            errors.brandError,
            ...(errors.extendedAttributeError
              ? Object.values(errors.extendedAttributeError)
              : []),
          ]
            .filter((msg): msg is string => Boolean(msg))
            .map((text) => ({ text }))
          if (errors.blackListError) {
            errorMessages.push({
              label: '项目管控',
              text: errors.blackListError,
            })
          }
          setPreCheckErrorMessages(errorMessages)
        }
      } else {
        // 预审接口调用失败，关闭弹框，继续正常提交流程
        setPreCheckVisible(false)
      }
    },
    [form, isEcommerceSupplier, runSubmit],
  )

  // 预审弹框 - 立即修改
  const handlePreCheckModify = useCallback(() => {
    setPreCheckVisible(false)
    // 将错误信息传递给主组件
    onModify?.(preCheckErrors)
  }, [onModify, preCheckErrors])

  // 预审弹框 - 稍后处理
  const handlePreCheckProcessLater = useCallback(() => {
    setPreCheckVisible(false)
    onProcessLater?.()
  }, [onProcessLater])

  // 预审弹框 - 确认提交物料（预审成功后）
  // 提交返回前弹框保持打开并置为提交中，避免用户以为没提交成功而重复发起
  const handlePreCheckSubmit = useCallback(async () => {
    if (!preCheckParams || submittingRef.current) {
      return
    }
    await runSubmit(preCheckParams)
    setPreCheckVisible(false)
  }, [preCheckParams, runSubmit])

  // 预审弹框 - 关闭
  const handlePreCheckCancel = useCallback(() => {
    if (submittingRef.current) {
      return
    }
    setPreCheckVisible(false)
  }, [])

  // 预审弹框组件
  const PreCheckModalComponent = (
    <PreCheckModal
      visible={preCheckVisible}
      status={preCheckStatus}
      errorList={preCheckErrorMessages}
      reviewer={preCheckReviewer}
      onCancel={handlePreCheckCancel}
      onModify={handlePreCheckModify}
      onProcessLater={handlePreCheckProcessLater}
      onSubmit={handlePreCheckSubmit}
      submitting={submitting}
    />
  )

  return {
    executePreCheck,
    PreCheckModalComponent,
    submitting,
  }
}
import React, { useState, useCallback, useRef } from 'react'
import { message } from 'antd'
import { FormInstance } from 'antd/lib/form'
import { materialTypeEnum } from '@/global/enum/product'
import { POST_PRODUCT_PRECHECK, PreCheckErrors } from '@/api/MaterialApi'

// 导出错误类型，供外部使用
export type { PreCheckErrors }
import PreCheckModal, {
  PreCheckErrorItem,
  PreCheckStatus,
} from '@/pages/goodsmanage/components/PreCheckModal'
import {
  preReviewOfMaterialsTypeEnum,
  preReviewOfMaterialsTypeEnumValue,
} from '@/global/enum/product'

export interface UsePreCheckOptions {
  form: FormInstance // 表单实例，用于获取productType
  onSubmit: (params: any) => Promise<void> // 实际提交函数
  onModify?: (errors: PreCheckErrors) => void // 立即修改回调，返回错误信息对象
  onProcessLater?: () => void // 稍后处理回调
}

export interface UsePreCheckReturn {
  // 执行预审的函数
  executePreCheck: (
    params: any,
    type: preReviewOfMaterialsTypeEnumValue,
  ) => Promise<void>
  // 预审弹框组件
  PreCheckModalComponent: React.ReactNode
  // 提交是否进行中，供调用页面禁用提交按钮
  submitting: boolean
}

/**
 * 物料预审 Hook
 * 用于在提交物料前进行预审校验
 */
export const usePreCheck = (options: UsePreCheckOptions): UsePreCheckReturn => {
  const { form, onSubmit, onModify, onProcessLater } = options

  // 预审弹框状态
  const [preCheckVisible, setPreCheckVisible] = useState(false)
  const [preCheckStatus, setPreCheckStatus] =
    useState<PreCheckStatus>('loading')
  const [preCheckErrors, setPreCheckErrors] = useState<PreCheckErrors>({})
  const [preCheckErrorMessages, setPreCheckErrorMessages] = useState<
    PreCheckErrorItem[]
  >([]) // 用于弹框显示的错误信息
  const [preCheckReviewer, setPreCheckReviewer] = useState<string>('')
  const [preCheckParams, setPreCheckParams] = useState<any>(null)
  // 提交进行中：物料提报接口耗时较长，期间必须挡住重复提交
  const [submitting, setSubmitting] = useState(false)
  // state 更新是异步的，同一轮事件里连点拿不到最新值，用 ref 即时兜住
  const submittingRef = useRef(false)

  /**
   * 提交并维护 submitting 状态，提交进行中重复调用直接忽略
   */
  const runSubmit = useCallback(
    async (params: any) => {
      if (submittingRef.current) {
        return
      }
      submittingRef.current = true
      setSubmitting(true)
      try {
        await onSubmit(params)
      } finally {
        submittingRef.current = false
        setSubmitting(false)
      }
    },
    [onSubmit],
  )

  /**
   * 判断是否是电商供应商物料
   */
  const isEcommerceSupplier = useCallback((): boolean => {
    const productType = form.getFieldValue('productType')
    return materialTypeEnum.getLabelByValue(productType) === '电商供应商物料'
  }, [form])

  /**
   * 执行预审
   */
  const executePreCheck = useCallback(
    async (params: any, type: preReviewOfMaterialsTypeEnumValue) => {
      // 如果不是电商供应商物料，直接提交
      if (!isEcommerceSupplier()) {
        await runSubmit(params)
        return
      }

      // 显示预审中弹框
      setPreCheckVisible(true)
      setPreCheckStatus('loading')
      setPreCheckParams(params)
      message.destroy()

      let preCheckParams: any = {
        preReviewOfMaterialsType: type,
      }
      if (preReviewOfMaterialsTypeEnum.isLabelsMatchValue(['新增物料'], type)) {
        preCheckParams = { ...preCheckParams, productAddParam: params }
      } else if (
        preReviewOfMaterialsTypeEnum.isLabelsMatchValue(['单条提报'], type)
      ) {
        preCheckParams = { ...preCheckParams, materialReportUpdateForm: params }
      } else if (
        preReviewOfMaterialsTypeEnum.isLabelsMatchValue(['提报驳回更新'], type)
      ) {
        preCheckParams = {
          ...preCheckParams,
          materialReportSaveNewForm: params,
        }
      }
      // 调用预审接口
      const preCheckRes = await POST_PRODUCT_PRECHECK(preCheckParams)
      if (preCheckRes.code === 0 && preCheckRes.data) {
        const { success, errorList, reviewer } = preCheckRes.data

        if (success) {
          // 预审成功
          setPreCheckStatus('success')
          setPreCheckReviewer(reviewer || '')
        } else {
          // 预审失败
          const errors: PreCheckErrors = errorList || {}
          setPreCheckStatus('failed')
          setPreCheckErrors(errors)
          // 提取所有错误信息用于弹框显示
          const errorMessages: PreCheckErrorItem[] = [
            errors.marketPriceError,
            errors.picturesError,
            errors.detailsPicturesError,
            errors.brandError,
            ...(errors.extendedAttributeError
              ? Object.values(errors.extendedAttributeError)
              : []),
          ]
            .filter((msg): msg is string => Boolean(msg))
            .map((text) => ({ text }))
          if (errors.blackListError) {
            errorMessages.push({
              label: '项目管控',
              text: errors.blackListError,
            })
          }
          setPreCheckErrorMessages(errorMessages)
        }
      } else {
        // 预审接口调用失败，关闭弹框，继续正常提交流程
        setPreCheckVisible(false)
      }
    },
    [form, isEcommerceSupplier, runSubmit],
  )

  // 预审弹框 - 立即修改
  const handlePreCheckModify = useCallback(() => {
    setPreCheckVisible(false)
    // 将错误信息传递给主组件
    onModify?.(preCheckErrors)
  }, [onModify, preCheckErrors])

  // 预审弹框 - 稍后处理
  const handlePreCheckProcessLater = useCallback(() => {
    setPreCheckVisible(false)
    onProcessLater?.()
  }, [onProcessLater])

  // 预审弹框 - 确认提交物料（预审成功后）
  // 提交返回前弹框保持打开并置为提交中，避免用户以为没提交成功而重复发起
  const handlePreCheckSubmit = useCallback(async () => {
    if (!preCheckParams || submittingRef.current) {
      return
    }
    await runSubmit(preCheckParams)
    setPreCheckVisible(false)
  }, [preCheckParams, runSubmit])

  // 预审弹框 - 关闭
  const handlePreCheckCancel = useCallback(() => {
    if (submittingRef.current) {
      return
    }
    setPreCheckVisible(false)
  }, [])

  // 预审弹框组件
  const PreCheckModalComponent = (
    <PreCheckModal
      visible={preCheckVisible}
      status={preCheckStatus}
      errorList={preCheckErrorMessages}
      reviewer={preCheckReviewer}
      onCancel={handlePreCheckCancel}
      onModify={handlePreCheckModify}
      onProcessLater={handlePreCheckProcessLater}
      onSubmit={handlePreCheckSubmit}
      submitting={submitting}
    />
  )

  return {
    executePreCheck,
    PreCheckModalComponent,
    submitting,
  }
}
